/* eslint-disable @next/next/no-img-element, react/no-unescaped-entities */
import clsx from 'clsx';
import { CldImage } from 'next-cloudinary';
import { Fragment, useEffect, useState } from 'react';

import type { FormQuery } from '@/api';
import { Form } from '@/components/form/Form';

import type { Cta, LpHomeContent } from './content';

type Props = {
  content: LpHomeContent;
  formData: FormQuery;
};

const CONTAINER = 'mx-auto max-w-[1800px] px-[clamp(20px,3vw,60px)]';

const CTA_BASE =
  'inline-block px-[36px] py-[16px] rounded-[8px] text-[13px] font-[600] uppercase tracking-[1px] no-underline transition-all duration-200 max-md:block max-md:w-full max-md:text-center';
const CTA_PRIMARY = clsx(
  CTA_BASE,
  'bg-rimac-energy text-white hover:bg-rimac-energy-dark',
);
const CTA_SECONDARY = clsx(
  CTA_BASE,
  'border-2 border-forest bg-transparent text-forest hover:border-rimac-energy hover:bg-rimac-energy hover:text-white',
);

/** Render an array of strings as visual lines separated by <br/>. */
const MultiLine = ({ lines }: { lines: string[] }) => (
  <>
    {lines.map((line, index) => (
      <Fragment key={line}>
        {index > 0 && <br />}
        {line}
      </Fragment>
    ))}
  </>
);

/** Extra anchor attributes for external (new-tab) CTAs. */
const externalProps = (cta: Cta) =>
  cta.external ? { target: '_blank', rel: 'noopener noreferrer' } : {};

/**
 * Updated homepage design (formerly the `lp-update.html` template), styled with
 * Tailwind using the shared design tokens configured in tailwind.config.js
 * (typography scale + brand colours aligned with @rimactechnology/rimac-ui-lib).
 * The hero title uses the unified `h1` token so it matches the deployed site.
 * Content is supplied via `content` so it can be sourced from Strapi later.
 */
export const LpHomePage = ({ content, formData }: Props) => {
  const { nav, hero, productOverview, productSections, footer } = content;

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Header background appears once the user scrolls past the hero edge.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll while the mobile menu overlay is open.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="min-h-screen bg-white font-sans text-black antialiased">
      {/* HEADER */}
      <header
        className={clsx(
          'fixed inset-x-0 top-0 z-[100] transition-colors duration-300',
          menuOpen
            ? 'bg-forest'
            : scrolled
            ? 'bg-rimac-energy'
            : 'bg-transparent',
        )}
        id="site-header"
      >
        <div className="flex h-[69px] w-full items-center justify-between overflow-hidden px-[clamp(20px,3vw,60px)]">
          <a href="#" className="flex h-[26px] w-[150px] shrink-0">
            <img
              src="/lp-update-assets/7de7f326-6789-411f-bd85-c59e968416b6.svg"
              alt="Rimac Energy"
              className="size-full object-contain"
            />
          </a>
          <nav>
            <ul className="flex list-none items-center max-lg:hidden">
              {nav.map((item) => (
                <li key={item.href}>
                  <a
                    href={item.href}
                    className="px-6 text-menu uppercase text-white transition-opacity duration-200 hover:opacity-60"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
          <button
            type="button"
            className="z-[110] hidden cursor-pointer flex-col gap-[6px] p-2 max-lg:flex"
            aria-label="Menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span
              className={clsx(
                'block h-[2px] w-6 rounded-[2px] bg-white transition-transform duration-300',
                menuOpen && 'translate-y-1 rotate-45',
              )}
            />
            <span
              className={clsx(
                'block h-[2px] w-6 rounded-[2px] bg-white transition-transform duration-300',
                menuOpen && '-translate-y-1 -rotate-45',
              )}
            />
          </button>
        </div>
        <div
          className={clsx(
            'fixed inset-0 z-[99] flex items-center justify-center bg-forest transition-opacity duration-300',
            menuOpen
              ? 'pointer-events-auto opacity-100'
              : 'pointer-events-none opacity-0',
          )}
          id="mobile-menu"
        >
          <nav className="flex flex-col items-center gap-8">
            {nav.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="text-[28px] font-light uppercase tracking-[-0.5px] text-white no-underline transition-opacity duration-200 hover:opacity-60"
                onClick={closeMenu}
              >
                {item.label}
              </a>
            ))}
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="relative h-[1200px] max-h-screen min-h-[560px] w-full overflow-hidden max-lg:h-auto max-lg:min-h-[85vh] max-md:min-h-[75vh]">
        <CldImage
          fill
          src={hero.backgroundImage.src}
          alt={hero.backgroundImage.alt}
          sizes="100vw"
          priority
          className="absolute inset-0 size-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(0,0,0,0.73)_0%,rgba(0,0,0,0.1)_73%)]" />
        <div className="absolute inset-x-0 bottom-[clamp(40px,4vw,80px)] mx-auto max-w-[1800px] px-[clamp(20px,3vw,60px)] max-lg:bottom-10 max-lg:px-8 max-md:bottom-8 max-md:px-5 max-s:bottom-6">
          <div className="max-w-[clamp(600px,50vw,960px)] px-9 max-lg:max-w-none max-lg:px-0">
            <h1 className="w-full text-balance text-h1-mobile font-normal text-white lg:text-h1 lg:font-normal">
              <MultiLine lines={hero.titleLines} />
            </h1>
            <p className="mt-[clamp(20px,2vw,36px)] max-w-[clamp(440px,35vw,640px)] text-p text-white max-lg:max-w-[480px] max-md:mt-5 max-md:max-w-none">
              {hero.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* PRODUCT OVERVIEW STRIP */}
      <section className="border-b border-black/[0.06] bg-white">
        <div className="mx-auto grid max-w-[1800px] grid-cols-4 max-lg:grid-cols-2 max-md:grid-cols-1">
          {productOverview.map((card) => (
            <a
              key={card.href}
              href={card.href}
              className="flex flex-col border-r border-forest/[0.08] p-[clamp(36px,3.2vw,64px)_clamp(24px,2.5vw,48px)] text-forest no-underline last:border-r-0 max-md:border-b max-md:border-r-0 max-md:border-forest/[0.06] max-md:p-[28px_20px] max-md:last:border-b-0 max-s:p-[24px_16px] max-lg:[&:nth-child(2)]:border-r-0"
            >
              <span className="mb-3 text-[10px] font-[600] uppercase tracking-[1.5px] text-rimac-energy">
                {card.category}
              </span>
              <h3 className="mb-[14px] text-[clamp(20px,1.6vw,30px)] font-light tracking-[-0.5px] text-forest">
                {card.name}
              </h3>
              <p className="flex-1 text-[clamp(13px,1vw,16px)] leading-normal text-body-gray">
                {card.description}
              </p>
              <span className="mt-6 text-[12px] font-[600] uppercase tracking-[0.8px] text-rimac-energy">
                Learn more &darr;
              </span>
            </a>
          ))}
        </div>
      </section>

      {/* PRODUCT SECTIONS */}
      {productSections.map((section) => (
        <Fragment key={section.id}>
          <section
            id={section.id}
            className={clsx(
              'grid grid-cols-2 max-lg:grid-cols-1',
              section.light ? 'bg-rimac-card-bg' : 'bg-forest',
            )}
          >
            <div
              className={clsx(
                'flex flex-col justify-center p-[clamp(48px,5.5vw,100px)] max-lg:p-[60px_40px] max-md:p-[40px_20px] max-s:p-[32px_16px]',
                section.reverse && 'lg:order-2',
              )}
            >
              <span className="mb-[clamp(16px,1.6vw,32px)] block text-[clamp(11px,0.8vw,14px)] font-[600] uppercase tracking-[2px] text-rimac-energy">
                {section.category}
              </span>
              <h2
                className={clsx(
                  'mb-[clamp(20px,2vw,36px)] text-balance text-[clamp(32px,3.2vw,60px)] leading-[1.12] tracking-[-1.2px] max-lg:text-[36px] max-lg:leading-[40px] max-md:mb-5 max-md:text-[28px] max-md:leading-[32px] max-s:text-[24px] max-s:leading-[28px]',
                  section.light
                    ? 'font-normal text-black'
                    : 'font-light text-white',
                )}
              >
                <MultiLine lines={section.headingLines} />
              </h2>
              <p
                className={clsx(
                  'max-w-[clamp(400px,34vw,600px)] text-p',
                  section.light ? 'text-black' : 'text-white',
                )}
              >
                {section.description}
              </p>
            </div>
            <div
              className={clsx(
                'relative overflow-hidden max-lg:min-h-[300px] max-md:min-h-[260px]',
                section.reverse && 'lg:order-1',
              )}
            >
              <CldImage
                fill
                src={section.image.src}
                alt={section.image.alt}
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="absolute inset-0 size-full object-cover object-center"
              />
            </div>
          </section>
          <section className="bg-white py-[clamp(64px,8vw,140px)] max-lg:py-12 max-md:py-10">
            <div className="mx-auto max-w-[1800px] px-[clamp(40px,5.5vw,100px)] max-lg:px-9 max-md:px-5">
              <div
                className={clsx(
                  'mb-[clamp(48px,5vw,88px)] grid gap-[clamp(24px,3.2vw,60px)] max-lg:gap-6 max-md:mb-9 max-md:grid-cols-1 max-md:gap-4',
                  section.uspGridFour
                    ? 'grid-cols-4 max-[1100px]:grid-cols-2'
                    : 'grid-cols-3 max-lg:grid-cols-2',
                )}
              >
                {section.usps.map((usp) => (
                  <div
                    key={usp.title}
                    className="rounded-[16px] border border-forest/[0.06] bg-rimac-card-bg p-[clamp(28px,2.8vw,52px)] shadow-[0_4px_20px_rgba(2,60,51,0.06)] transition-all duration-300 hover:-translate-y-1 hover:border-rimac-energy hover:shadow-[0_12px_40px_rgba(2,60,51,0.1)] max-md:p-[28px_24px] max-s:p-[24px_20px]"
                  >
                    <h3 className="mb-3 text-[clamp(18px,1.5vw,26px)] font-[600] leading-[1.3] text-rimac-energy">
                      {usp.title}
                    </h3>
                    <p className="text-[clamp(14px,1.05vw,17px)] leading-[1.6] text-black">
                      {usp.text}
                    </p>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center gap-6 max-md:flex-col max-md:gap-3">
                {section.primaryCta && (
                  <a
                    className={CTA_PRIMARY}
                    href={section.primaryCta.href}
                    {...externalProps(section.primaryCta)}
                  >
                    {section.primaryCta.label}
                  </a>
                )}
                <a className={CTA_SECONDARY} href="#contact">
                  {content.getInTouchLabel}
                </a>
              </div>
            </div>
          </section>
        </Fragment>
      ))}

      {/* CONTACT — uses the existing CMS-backed Form (reCAPTCHA + submission) */}
      <section id="contact">
        <Form data={formData} />
      </section>

      {/* FOOTER */}
      <footer className="bg-white">
        <div
          className={clsx(
            CONTAINER,
            'pb-[clamp(32px,2.8vw,52px)] pt-[clamp(48px,5.5vw,100px)] max-md:pb-8 max-md:pt-10',
          )}
        >
          <div className="mb-[clamp(48px,5.5vw,100px)] h-[clamp(36px,3vw,52px)] w-[clamp(180px,14vw,260px)]">
            <img
              src={footer.logo.src}
              alt={footer.logo.alt}
              className="size-full object-contain"
            />
          </div>
          <div className="flex w-full gap-0 max-lg:flex-wrap max-md:flex-col max-md:gap-[36px]">
            <div className="min-w-[320px] shrink-0 max-lg:mb-9 max-lg:w-full max-md:min-w-0">
              <div className="mb-[clamp(28px,3vw,52px)] text-[clamp(18px,1.5vw,26px)] font-medium leading-none tracking-[-0.8px]">
                {footer.brandName}
              </div>
              <a
                href={`mailto:${footer.email}`}
                className="text-[14.9px] font-normal leading-[22.4px] tracking-[-0.3px] hover:opacity-60"
              >
                {footer.email}
              </a>
              <div className="mt-9">
                <a href={footer.linkedin.href} className="hover:opacity-60">
                  <img
                    src={footer.linkedin.iconSrc}
                    alt="LinkedIn"
                    className="size-8"
                  />
                </a>
              </div>
            </div>
            {footer.columns.map((column) => (
              <div className="flex-1" key={column.heading}>
                <div className="mb-10 text-[12.8px] font-normal uppercase leading-[15.3px] tracking-[0.3px] max-md:mb-6">
                  {column.heading}
                </div>
                <ul className="flex list-none flex-col gap-[15.5px]">
                  {column.links.map((link) => (
                    <li key={link.href}>
                      <a
                        href={link.href}
                        className="text-[clamp(18px,1.5vw,26px)] font-light leading-[1.3] tracking-[-1.2px] transition-opacity duration-200 hover:opacity-60 max-md:text-[18px] max-md:leading-[24px] max-md:tracking-[-0.5px]"
                      >
                        {link.lines ? (
                          <MultiLine lines={link.lines} />
                        ) : (
                          link.label
                        )}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-12 flex items-center justify-between border-t border-black pt-[41px] max-md:mt-10 max-md:flex-col max-md:items-start max-md:gap-5 max-md:pt-6">
            <span className="min-w-[260px] text-[14.6px] font-normal leading-[22.4px] tracking-[-0.3px]">
              {footer.copyright}
            </span>
            <ul className="flex flex-1 list-none max-md:flex-wrap max-md:gap-x-4 max-md:gap-y-1">
              {footer.legal.map((link) => (
                <li key={link.href} className="flex-1">
                  <a
                    href={link.href}
                    className="text-[14.8px] font-normal leading-[22.4px] tracking-[-0.3px] transition-opacity duration-200 hover:opacity-60"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LpHomePage;
